import { IJsonQuery } from '@cian/api-models/common/json_query';

import {
  Action,
  AnyAction,
  applyMiddleware,
  combineReducers,
  compose,
  createStore as _createStore,
  Reducer,
} from 'redux';
import thunkMiddleware from 'redux-thunk';

import { defaultState as pageDefaultState } from '../../constants/page';
import {
  clearGeoReducer,
  clearReducer,
  geocodeReducer,
  metaReducer,
  selectCoworkingOfferTypeReducer,
  selectGeoReducer,
  selectOfferTypeReducer,
  selectRoomTypeReducer,
  setAreaReducer,
  setBedroomsCountReducer,
  setBSCentersReducer,
  setBuilderReducer,
  setDealTypeReducer,
  setJsonQueryReducer,
  setLandAreaReducer,
  setPosessorReducer,
  setPriceReducer,
  setPriceTypeReducer,
  setRegionReducer,
  setVillageReducer,
  setWorkplaceCountReducer,
  suggestionsReducer,
  setCoworkingIdReducer,
} from '../../reducers';
import { mortgageFilters } from '../../reducers/mortgage';
import { specialTabsReducer } from '../../reducers/specialTabs';
import { searchRealtorsReducer } from '../../reducers/searchRealtors';
import { oldReducers } from '../../reducers/reducers';
import { IFeaturesState } from '../../types/features';
import { IFiltersState } from '../../types/filters';
import { IPageState } from '../../types/page';
import {
  IReduxState,
  IReduxStore,
  IThunkContext,
  TReduxActions,
  TThunkDispatch,
  TThunkMiddleware,
} from '../../types/redux';

import { createSimpleReducer } from './createSimpleReducer';

let storeReminder: IReduxStore;

export interface ICreateReduxStoreOptions {
  context: IThunkContext;
  initialState?: Partial<IReduxState>;
  debugEnabled?: boolean;
}

const featuresDefaultState: IFeaturesState = {
  isDobroshriftEnabled: false,
  isFindAgentTabEnabled: false,
};

export function createReduxStore(options: ICreateReduxStoreOptions) {
  const reducers = combineReducers<IReduxState, TReduxActions>({
    ...oldReducers,
    filters: combineReducers<IFiltersState, TReduxActions>({
      jsonQuery: reduceReducers<IJsonQuery, TReduxActions>(
        setJsonQueryReducer,
        setDealTypeReducer,
        selectOfferTypeReducer,
        selectCoworkingOfferTypeReducer,
        setWorkplaceCountReducer,
        selectRoomTypeReducer,
        setPriceReducer,
        setAreaReducer,
        setLandAreaReducer,
        setPosessorReducer,
        setBedroomsCountReducer,
        setRegionReducer,
        setVillageReducer,
        setBSCentersReducer,
        selectGeoReducer,
        clearGeoReducer,
        setBuilderReducer,
        setPriceTypeReducer,
        setCoworkingIdReducer,
        /** Всегда последний */
        clearReducer,
      ),
      meta: metaReducer,
      suggestions: suggestionsReducer,
      geocode: geocodeReducer,
    }),
    page: createSimpleReducer<IPageState>(pageDefaultState),
    features: createSimpleReducer(featuresDefaultState),
    mortgage: mortgageFilters,
    isCoworkingHelperVisible: createSimpleReducer<boolean>(false),
    specialTabs: specialTabsReducer,
    searchRealtors: searchRealtorsReducer,
  });

  const composeEnhancers = options.debugEnabled ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;

  const storeEnchancers = composeEnhancers(
    applyMiddleware(thunkMiddleware.withExtraArgument(options.context) as TThunkMiddleware),
  );

  let createStore: () => IReduxStore;
  if (options.initialState) {
    createStore = () =>
      _createStore<IReduxState, TReduxActions, { dispatch: TThunkDispatch }, {}>(
        reducers,
        options.initialState as IReduxState,
        storeEnchancers,
      );
  } else {
    createStore = () =>
      _createStore<IReduxState, TReduxActions, { dispatch: TThunkDispatch }, {}>(reducers, storeEnchancers);
  }

  if (process.env.NODE_ENV === 'development' && options.debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(reducers);

    return storeReminder;
  }

  return createStore();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reduceReducers<S, A extends Action<any> = AnyAction>(...reducers: Reducer<S, A>[]): Reducer<S, A> {
  return (prevState: S | undefined, action: A): S =>
    reducers.reduce((newState, reducer) => reducer(newState, action), prevState) as S;
}
